import React, { useEffect, useState } from 'react';
import { Layout, Dropdown, Select, Button, Drawer, message } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faInbox,
  faListCheck,
  faPhoneVolume,
  faAddressBook,
  faChartLine,
  faCalendarDays,
  faBuilding,
  faDiagramProject,
  faRightFromBracket,
  faCircleCheck,
  faCircleMinus,
  faBars,
  faXmark,
  faChevronLeft,
  faDownload,
  faBell,
  faUserShield,
  faGear
} from '@fortawesome/free-solid-svg-icons';
import SlaBell from '../components/SlaBell';
import { useAuth } from '../context/AuthContext';
import { portalApi } from '../services/portalApi';
import { applyBranding } from '../utils/branding';
import { useConversationsQuery } from '../queries/messages';
import { useNeedsYou } from '../queries/actions';
import { useAgentsQuery } from '../queries/agents';
import { useMaxWidth, usePhone } from '../hooks/usePhone';
import { TABLET } from '../theme/tokens';
import zeroLogo from '../assets/images/zero-logo-small.png';
import useInstallPrompt from '../pwa/useInstallPrompt';
import usePushOnboarding from '../pwa/usePushOnboarding';
import { getPushStatus, PUSH_STATUS_EVENT, subscribeToPush } from '../pwa/push';
import { ACCESS_ROLES } from '../utils/roles';
import {
  Shell,
  PreviewBar,
  Brand,
  Header,
  StatusChip,
  SideFoot,
  MenuIdentity,
  NavList,
  NavItem,
  NavCount,
  TabBar
} from './AppLayout.styled';

const { Sider, Content } = Layout;

function InboxCount() {
  const { data } = useConversationsQuery();
  const n = (data?.conversations || []).filter((c) => c.unread > 0).length;
  return n ? <NavCount $gray>{n}</NavCount> : null;
}

function ActionsCount() {
  const { user } = useAuth();
  const total = useNeedsYou(user).length;
  return total ? <NavCount>{total}</NavCount> : null;
}

const NAV_BLOCKS = [
  [
    { key: '/', icon: faHouse, label: 'Home' },
    { key: '/todos', icon: faListCheck, label: 'Agent Actions', Badge: ActionsCount },
    { key: '/inbox', icon: faInbox, label: 'Inbox', perm: 'manage_messages', Badge: InboxCount },
    { key: '/contacts', icon: faAddressBook, label: 'Contacts' },
    { key: '/calls', icon: faPhoneVolume, label: 'Call Logs', perm: 'view_calls' },
    { key: '/reports', icon: faChartLine, label: 'Analytics', perm: 'view_reports' }
  ],
  [
    { key: '/scheduling', icon: faCalendarDays, label: 'Calendar', perm: 'manage_bookings', noStaff: true },
    { key: '/property', icon: faBuilding, label: 'Property' },
    { key: '/pipelines', icon: faDiagramProject, label: 'Pipelines', noStaff: true }
  ]
];

const TAB_KEYS = ['/', '/inbox', '/todos', '/calls', '/contacts'];

const HIDDEN_PAGES = [{ key: '/automations', label: 'Automations' }];

const matches = (key, pathname) => (key === '/' ? pathname === '/' : pathname.startsWith(key));

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, can, isPlatformAdmin, previewRole, setPreviewRole } = useAuth();
  const [org, setOrg] = useState(null);
  const { data: agents = [] } = useAgentsQuery();
  const phone = usePhone();
  const drawerNav = useMaxWidth(TABLET);
  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [pushStatus, setPushStatus] = useState('unsupported');
  const { canInstall, promptInstall } = useInstallPrompt();
  usePushOnboarding(user?._id || user?.id);

  useEffect(() => setNavOpen(false), [location]);

  const impersonating = !!localStorage.getItem('impersonateOrg');
  const exitImpersonation = () => {
    localStorage.removeItem('impersonateOrg');
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  useEffect(() => {
    portalApi
      .me()
      .then((r) => {
        setOrg(r.organization || null);
        applyBranding(r.organization?.branding || {});
        document.title = r.organization?.name
          ? `${r.organization.name} | Agent Zero Property Management`
          : 'Agent Zero Property Management';
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let current = true;
    const changed = (event) => setPushStatus(event.detail);
    getPushStatus().then((status) => current && setPushStatus(status)).catch(() => {});
    window.addEventListener(PUSH_STATUS_EVENT, changed);
    return () => {
      current = false;
      window.removeEventListener(PUSH_STATUS_EVENT, changed);
    };
  }, []);

  const agentZeroMode = isPlatformAdmin && impersonating && !previewRole;
  const allowed = (n) => (!n.perm || can(n.perm)) && !(n.noStaff && user?.role === 'staff');
  const blocks = NAV_BLOCKS.map((b) => b.filter(allowed)).filter((b) => b.length);
  if (agentZeroMode) blocks.push([{ key: '/settings', icon: faGear, label: 'Settings' }]);

  const current = blocks
    .flat()
    .concat(HIDDEN_PAGES)
    .filter((n) => matches(n.key, location.pathname))
    .sort((a, b) => b.key.length - a.key.length)[0];
  const selectedKey = current && !HIDDEN_PAGES.includes(current) ? current.key : null;
  const tabs = TAB_KEYS.map((k) => blocks.flat().find((n) => n.key === k)).filter(Boolean);

  const initials =
    `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U';

  const deviceItems = [
    ...(canInstall
      ? [{ key: 'install', icon: <FontAwesomeIcon icon={faDownload} />, label: 'Install app' }]
      : []),
    ...(!impersonating && pushStatus !== 'unsupported'
      ? [{
          key: 'notifications',
          icon: <FontAwesomeIcon icon={faBell} />,
          label:
            pushStatus === 'subscribed'
              ? 'Notifications on'
              : pushStatus === 'denied'
                ? 'Notifications blocked'
                : 'Enable notifications',
          disabled: pushStatus === 'subscribed' || pushStatus === 'denied'
        }]
      : [])
  ];

  const userMenu = {
    items: [
      {
        key: 'identity',
        disabled: true,
        label: (
          <MenuIdentity>
            <div className="mi-name">{user?.fullName || 'User'}</div>
            <div className="mi-email">{user?.email}</div>
          </MenuIdentity>
        )
      },
      { type: 'divider' },
      ...deviceItems,
      ...(deviceItems.length ? [{ type: 'divider' }] : []),
      {
        key: 'logout',
        icon: <FontAwesomeIcon icon={faRightFromBracket} />,
        label: 'Log out',
        danger: true
      }
    ],
    onClick: async ({ key }) => {
      if (key === 'logout') {
        await logout();
        navigate('/login');
      } else if (key === 'install') {
        await promptInstall();
      } else if (key === 'notifications') {
        try {
          await subscribeToPush();
          const status = await getPushStatus();
          setPushStatus(status);
          if (status === 'subscribed') message.success('Notifications are on');
          else if (status === 'denied') message.info('Notifications are blocked in your browser settings.');
        } catch (err) {
          message.error(err.message || 'Could not enable notifications');
        }
      } else if (key.startsWith('/')) {
        navigate(key);
      }
    }
  };

  const orgName = org?.name || 'Your properties';
  const agent = agents.find((a) => a.status === 'active') || agents[0];
  const agentName = agent?.name || 'Assistant';
  const agentOn = agent?.status === 'active';
  const statusChip = (
    <StatusChip $on={agentOn} title={agentOn ? `${agentName} is on` : `${agentName} is off`}>
      <FontAwesomeIcon icon={agentOn ? faCircleCheck : faCircleMinus} />
      <span className="sc-text">
        {agentOn ? `${agentName} is on · answering every call` : `${agentName} is off`}
      </span>
    </StatusChip>
  );

  const nav = (
    <>
      <Brand>
        <img src={zeroLogo} alt="Agent Zero" />
        <div>
          <div className="br-name">Agent Zero</div>
          <div className="br-env">Property Management</div>
        </div>
        {drawerNav && (
          <button className="br-close" type="button" aria-label="Close menu" onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </Brand>

      {phone && statusChip}

      <NavList aria-label="Main navigation">
        {blocks.map((b, i) => (
          <React.Fragment key={b[0].key}>
            {i > 0 && <div className="nav-div" role="separator" />}
            {b.map(({ key, icon, label, Badge }) => (
              <NavItem
                key={key}
                type="button"
                aria-current={selectedKey === key ? 'page' : undefined}
                onClick={() => navigate(key)}
              >
                <FontAwesomeIcon icon={icon} />
                <span className="nav-label">{label}</span>
                {Badge && <Badge />}
              </NavItem>
            ))}
          </React.Fragment>
        ))}
      </NavList>

      <SideFoot>
        <Dropdown menu={userMenu} placement="topLeft" trigger={['click']}>
          <button className="sf-user" type="button">
            <span className="sf-avatar">{initials}</span>
            <span className="sf-name">{`${initials} · ${user?.fullName || 'User'}`}</span>
          </button>
        </Dropdown>
      </SideFoot>
    </>
  );

  const roleOptions = [
    { value: '', label: 'Agent Zero Team' },
    ...[...ACCESS_ROLES].reverse().map((r) => ({ value: r.value, label: r.label }))
  ];

  const previewBar = impersonating && (
    <PreviewBar>
      <span className="pb-label">
        <FontAwesomeIcon icon={faUserShield} />
        Admin preview
        <span className="pb-org">· {orgName}</span>
      </span>
      <span className="pb-actions">
        {isPlatformAdmin && (
          <>
            <span className="pb-as">Viewing as</span>
            <Select
              size="small"
              style={{ minWidth: 150 }}
              value={previewRole || ''}
              options={roleOptions}
              onChange={(v) => setPreviewRole(v || null)}
            />
          </>
        )}
        <Button size="small" onClick={exitImpersonation}>
          Exit
        </Button>
      </span>
    </PreviewBar>
  );

  return (
    <Shell>
      {previewBar}
      <Layout>
        {drawerNav ? (
          <Drawer
            open={navOpen}
            onClose={() => setNavOpen(false)}
            placement="left"
            width={phone ? '86%' : 280}
            closable={false}
            styles={{
              body: { padding: phone ? 14 : 0, gap: phone ? 10 : 0, display: 'flex', flexDirection: 'column' },
              mask: phone ? { background: 'rgba(11, 20, 38, 0.35)' } : undefined,
              wrapper: phone ? { boxShadow: '8px 0 30px rgba(0, 0, 0, 0.15)' } : undefined
            }}
          >
            {nav}
          </Drawer>
        ) : (
          <Sider width={264} className="app-sider">
            {nav}
          </Sider>
        )}

        <Layout className="app-main">
          <Header>
            {phone && detail ? (
              <button className="hd-menu" type="button" aria-label="Back" onClick={detail.onBack}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            ) : (
              drawerNav && (
                <button className="hd-menu" type="button" aria-label="Open menu" onClick={() => setNavOpen(true)}>
                  <FontAwesomeIcon icon={faBars} />
                </button>
              )
            )}
            <div className="hd-crumb">
              <span className="hd-org">{`${orgName} / `}</span>
              <b>{(phone && detail?.title) || current?.label || 'Home'}</b>
            </div>
            {!phone && statusChip}
            <SlaBell />
          </Header>

          <Content className="app-content" style={{ padding: phone ? (detail ? 0 : 12) : 24, overflowX: 'hidden', overflowY: 'auto' }}>
            <Outlet context={{ setDetail }} />
          </Content>
          {phone && (
            <TabBar aria-label="Quick navigation">
              {tabs.map(({ key, icon, label, Badge }) => (
                <button
                  key={key}
                  type="button"
                  aria-current={selectedKey === key ? 'page' : undefined}
                  onClick={() => navigate(key)}
                >
                  <span className="tb-ic">
                    <FontAwesomeIcon icon={icon} />
                  </span>
                  {label}
                  {Badge && <Badge />}
                </button>
              ))}
            </TabBar>
          )}
        </Layout>
      </Layout>
    </Shell>
  );
}
